import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";

/* Generic node-anchored popover for the Protocol Network. One instance per
   topology layout, anchored beside the active node in px space. Collision
   order: stay inside visualization bounds first, avoid the central identity
   where practical. Absolutely positioned inside the visualization wrapper —
   never shifts document layout. */

export interface PopoverRow { label: string; value: string; color?: string }
export interface PopoverData {
  eyebrow: string; title: string; accent: string;
  rows: PopoverRow[];
  link?: { label: string; to: string; onClick?: () => void };
}
export interface PopoverAnchor { x: number; y: number; r: number }

const GAP = 12;
const PAD = 4;
type Placement = "right" | "left" | "above" | "below";

export const NetworkNodePopover = ({ containerRef, viewBoxW, anchor, avoid, data, obstacles = [], compact = false, onEnter, onLeave }: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  viewBoxW: number;
  anchor: PopoverAnchor;
  avoid: PopoverAnchor;
  data: PopoverData;
  obstacles?: PopoverAnchor[];
  compact?: boolean;
  onEnter?: () => void;
  onLeave?: () => void;
}) => {
  const reduced = useReducedMotion();
  const elRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [tick, setTick] = useState(0);

  useLayoutEffect(() => {
    const onResize = () => setTick((t) => t + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useLayoutEffect(() => {
    const box = containerRef.current?.getBoundingClientRect();
    const el = elRef.current;
    if (!box || !el || box.width < 10) { setPos(null); return; }
    const scale = box.width / viewBoxW;
    const W = el.offsetWidth, H = el.offsetHeight;
    const maxL = Math.max(PAD, box.width - W - PAD), maxT = Math.max(PAD, box.height - H - PAD);
    const clamp = (l: number, t: number) => ({ left: Math.min(Math.max(l, PAD), maxL), top: Math.min(Math.max(t, PAD), maxT) });
    const circleOverlap = (l: number, t: number, ox: number, oy: number, or_: number) => {
      const px = Math.min(Math.max(ox * scale, l), l + W), py = Math.min(Math.max(oy * scale, t), t + H);
      return Math.max(0, or_ * scale + 8 - Math.hypot(ox * scale - px, oy * scale - py));
    };
    // Weighted collisions: never cover the anchor node (x3), keep the central
    // identity clear (x1.5); other nodes are dimmed while a popover is open,
    // so overlapping them is least harmful (x0.5).
    const collide = (l: number, t: number) =>
      3 * circleOverlap(l, t, anchor.x, anchor.y, anchor.r) +
      1.5 * circleOverlap(l, t, avoid.x, avoid.y, avoid.r) +
      0.5 * obstacles.reduce((s, o) => s + circleOverlap(l, t, o.x, o.y, o.r), 0);
    const labelClear = 40 * scale; // node name/metric labels render below the circle
    const awayRight = anchor.x >= avoid.x;
    const order: Placement[] = awayRight ? ["right", "left", "above", "below"] : ["left", "right", "above", "below"];
    const raw: Record<Placement, { l: number; t: number }> = {
      right: { l: anchor.x * scale + anchor.r * scale + GAP, t: anchor.y * scale - H / 2 },
      left: { l: anchor.x * scale - anchor.r * scale - GAP - W, t: anchor.y * scale - H / 2 },
      above: { l: anchor.x * scale - W / 2, t: anchor.y * scale - anchor.r * scale - GAP - H },
      below: { l: anchor.x * scale - W / 2, t: anchor.y * scale + anchor.r * scale + labelClear + GAP },
    };
    let best: { left: number; top: number; score: number } | null = null;
    for (const p of order) {
      const fitsRaw = raw[p].l >= PAD && raw[p].l <= maxL && raw[p].t >= PAD && raw[p].t <= maxT;
      const c = clamp(raw[p].l, raw[p].t);
      const score = collide(c.left, c.top) + (fitsRaw ? 0 : 6);
      if (score === 0) { best = { ...c, score }; break; }
      if (!best || score < best.score) best = { ...c, score };
    }
    setPos(best ? { left: best.left, top: best.top } : null);
  }, [containerRef, viewBoxW, anchor.x, anchor.y, anchor.r, avoid.x, avoid.y, avoid.r, data.title, compact, tick]);

  const dirInit = reduced ? {} : anchor.x >= avoid.x ? { x: -6 } : { x: 6 };

  return (
    <motion.div
      ref={elRef}
      role="status" aria-live="polite"
      data-testid="network-node-popover"
      onMouseEnter={onEnter} onMouseLeave={onLeave}
      className={`absolute z-20 rounded-[14px] bg-[#0a0a10]/[0.97] border border-[#26283c] shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[6px] ${
        compact ? "w-[158px] px-[12px] py-[10px]" : "w-[178px] px-[14px] py-[12px]"}`}
      style={{ left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? "visible" : "hidden" }}
      initial={{ opacity: 0, ...dirInit }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      exit={{ opacity: 0, transition: { duration: 0.1 } }}
      transition={{ duration: reduced ? 0.12 : 0.18, ease: [0.22, 1, 0.36, 1] }}>
      <div className="flex items-center gap-[6px]">
        <span className="w-[6px] h-[6px] rounded-full shrink-0" style={{ background: data.accent }} aria-hidden />
        <p className="nl-geist text-[8px] leading-[11px] font-semibold tracking-[0.14em] text-[#9696a8] uppercase">
          {data.eyebrow}
        </p>
      </div>
      <p className="nl-sg text-[16px] leading-[21px] font-semibold text-white mt-[5px] truncate" data-testid="network-popover-title">
        {data.title}
      </p>
      <div className="mt-[8px] space-y-[6px]">
        {data.rows.map((r) => (
          <div key={r.label} className="flex items-baseline justify-between gap-[8px]">
            <span className="nl-geist text-[8px] leading-[11px] font-semibold tracking-[0.06em] text-[#9696a8] uppercase">
              {r.label}
            </span>
            <span className="nl-mono text-[11px] leading-[14px] font-semibold shrink-0" style={{ color: r.color || "#f6f7fc" }}>
              {r.value}
            </span>
          </div>
        ))}
      </div>
      {data.link && (
        <Link to={data.link.to} onClick={data.link.onClick}
          data-testid="network-popover-link"
          className="nl-geist block text-[9px] leading-[13px] font-semibold mt-[10px] hover:translate-x-[2px] transition-transform duration-200"
          style={{ color: data.accent }}>
          {data.link.label}
        </Link>
      )}
    </motion.div>
  );
};
